"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import {
  LayoutGrid,
  Hammer,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  Circle,
  Shield,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navigation = [
  { name: "Applications", href: "/apps", icon: LayoutGrid },
  { name: "Builds", href: "/builds", icon: Hammer },
  { name: "Teams", href: "/teams", icon: Users },
  { name: "Billing", href: "/billing", icon: CreditCard },
];

function TeamSwitcher() {
  const { teams, currentTeam, setCurrentTeam } = useAuth();

  if (teams.length <= 1) {
    return currentTeam ? (
      <div className="flex items-center gap-2.5 rounded-lg px-3 py-2">
        <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-xs font-semibold text-violet-400">
          {currentTeam.name.charAt(0).toUpperCase()}
        </div>
        <span className="truncate text-sm font-medium">{currentTeam.name}</span>
      </div>
    ) : null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent/50">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-xs font-semibold text-violet-400">
            {currentTeam?.name.charAt(0).toUpperCase()}
          </div>
          <span className="flex-1 truncate text-sm font-medium">{currentTeam?.name}</span>
          <ChevronsUpDown className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {teams.map((team) => (
          <DropdownMenuItem
            key={team.id}
            onClick={() => setCurrentTeam(team)}
            className="flex items-center gap-2"
          >
            <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-[10px] font-semibold text-violet-400">
              {team.name.charAt(0).toUpperCase()}
            </div>
            <span className="flex-1 truncate">{team.name}</span>
            {team.id === currentTeam?.id && <Check className="size-4 text-violet-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarContent() {
  const pathname = usePathname();
  const { user, logout, isAdmin } = useAuth();

  return (
    <div className="flex h-full w-64 flex-col bg-sidebar border-r border-border">
      {/* Logo */}
      <div className="flex h-14 items-center px-4">
        <Link href="/">
          <Image src="/logo.svg" alt="BuildMagic" width={180} height={53} className="h-9 w-auto" />
        </Link>
      </div>

      <Separator />

      {/* Team switcher */}
      <div className="px-3 pt-3">
        <TeamSwitcher />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-3 py-3">
        {navigation.map((item) => {
          const isActive =
            pathname === item.href || pathname.startsWith(item.href + "/");
          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-violet-500/10 text-violet-400"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )}
            >
              <item.icon className="size-4 shrink-0" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <Separator />

      {/* Bottom section */}
      <div className="space-y-0.5 px-3 py-3">
        {/* System status */}
        <Link
          href="/status"
          target="_blank"
          className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <Circle className="size-2 fill-green-500 text-green-500" />
          System Status
        </Link>

        {/* Admin link */}
        {user && isAdmin && (
          <Link
            href="/admin"
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              pathname === "/admin" || pathname.startsWith("/admin/")
                ? "bg-violet-500/10 text-violet-400"
                : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
            )}
          >
            <Shield className="size-4 shrink-0" />
            Admin
          </Link>
        )}

        {/* Settings + user */}
        {user && (
          <>
            <Link
              href="/settings"
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                pathname === "/settings" || pathname.startsWith("/settings/")
                  ? "bg-violet-500/10 text-violet-400"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )}
            >
              <Settings className="size-4 shrink-0" />
              Settings
            </Link>

            <div className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1 truncate">
                <p className="truncate text-sm font-medium">{user.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {user.email}
                </p>
              </div>
              <button
                onClick={logout}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
                title="Sign out"
              >
                <LogOut className="size-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:block">
        <SidebarContent />
      </aside>

      {/* Mobile hamburger button */}
      <div className="fixed top-0 left-0 z-40 flex h-14 items-center px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="size-5" />
              <span className="sr-only">Open navigation</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0" showCloseButton={false}>
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
